import { amorphicStatic } from '@haventech/amorphic';
import { Response } from 'express';
import { Utils } from '../../js/Utils';
import { Policy } from '../../js/customer/Policy';
import { ProductType, StreamableDoc, JuvenilePropsByAge, StateKeyValuePairs } from '../interfaces/CommonTypes';
import { Product, RiderConstants, LowFaceWholeLife } from './Product';
import { RateClasses, RateClassObj } from './RateClasses';
import { Assumptions } from './Assumptions';
import { MetadataNode } from '../MetadataNode';
import { AvailableStateRegistry } from '../registries/AvailableStateRegistry';
import { UICategory, UILoggerSingleton } from '../logger/UILoggerSingleton';

export class CommonUtils {
  static readonly DOCUSIGN_REDIRECT_CLIENT_USER_ID: number = 1000;
  private static readonly annualRegex = /annual/i;
  private static readonly monthRegex = /month/i;
  private static readonly semiAnnualRegex = /SemiAnnual/i;
  private static readonly quarterlyRegex = /Quarterly/i;

  /**
   * Returns a string with paymentFrequency convention expected by a policy object (e.g., monthly, annual)
   * TODO HL-24153: This method should support all potential paymentFrequency values
   */
  static makePaymentFrequencySafeForPolicy(input: string): string {
    if (input === null || input === undefined || input === '') {
      return input;
    }

    if (this.semiAnnualRegex.test(input)) {
      return 'semi-annual';
    } else if (this.annualRegex.test(input)) {
      return 'annual';
    } else if (this.monthRegex.test(input)) {
      return 'monthly';
    } else if (this.quarterlyRegex.test(input)) {
      return 'quarterly';
    } else {
      throw Error(`Can't make ${input} safe for a paymentFrequency on a policy`);
    }
  }

  static isFinalPremiumWorseThanTlicPremium(policy: Policy): boolean {
    if (!policy.tLICPremiumBreakdown.base) {
      return true;
    }
    const tlicPrem = policy.tLICPremiumBreakdown.getTotal();
    const finalPremBd = policy.premiumBreakdown;
    const finalPrem = Utils.round(finalPremBd.getTotal() - finalPremBd.waiver, 2);

    return tlicPrem < finalPrem;
  }

  static invalidJuvenilePropsByAge(): JuvenilePropsByAge {
    return {
      '16.5': {
        personalHistory: ['hivPositiveWhichAddress', 'hivPositiveAddressPPName'],
        riskFactors: [],
      },
      '14': {
        personalHistory: [],
        riskFactors: ['isPregnant'],
      },
      '10': {
        personalHistory: ['hadAlcoholAbuse', 'hadAlcoholAbuseDiagnosis', 'hadDrugUse', 'hadDrugUseDetails'],
        riskFactors: [
          'whenLastSmokedOrUsedMarijuana',
          'usesCigarettes',
          'whenLastSmokedCigarettes',
          'usesCigars',
          'usesCigarsCurrently',
          'usesCigarsFrequency',
          'whenLastSmokedCigars',
          'usedCigarsFrequency',
          'usesMarijuana',
          'whenLastUsedMarijuana',
          'recreationalOrMedicalMarijuanaUser',
          'usesMarijuanaFrequency',
          'medicinalMarijuanaCondition',
          'medicinalMarijuanaDiagnosis',
          'usesPipe',
          'currUsesOtherTobacco',
          'quitTobaccoPeriod',
          'useTobaccoCessationMeds',
          'useAlcohol',
          'useAlcoholBinge',
          'howManyAlcohol',
        ],
      },
    };
  }

  static isYoungerThanAge(dateOfBirth: Date, age: number, submittedAt?: Date): boolean {
    const dob = new Date(dateOfBirth);
    dob.setHours(0, 0, 0, 0);
    return this.getDobForAge(age, submittedAt) < dob;
  }

  static isYoungerThanOrEqualToAge(dateOfBirth: Date, age: number): boolean {
    const dob = new Date(dateOfBirth);
    dob.setHours(0, 0, 0, 0);
    return this.getDobForAge(age) <= dob;
  }

  static isOlderThanAge(dateOfBirth: Date, age: number): boolean {
    const dob = new Date(dateOfBirth);
    dob.setHours(0, 0, 0, 0);
    return this.getDobForAge(age) > dob;
  }

  static isOlderThanOrEqualToAge(dateOfBirth: Date, age: number, submittedAt?: Date): boolean {
    const dob = new Date(dateOfBirth);
    dob.setHours(0, 0, 0, 0);
    return this.getDobForAge(age, submittedAt) >= dob;
  }

  static getDobForAge(age: number, submittedAt?: Date): Date {
    const referenceDate: Date = submittedAt ? submittedAt : new Date();
    referenceDate.setHours(0, 0, 0, 0);
    const ageInMonths: number = age * 12;
    const ageDate: Date = Utils.offsetDate(referenceDate, -ageInMonths, 'months');
    ageDate.setHours(0, 0, 0, 0);
    return ageDate;
  }

  static isEcpProduct(productCode: string): boolean {
    return (
      productCode === Product.products.CASTermECP.code ||
      productCode === Product.products.MMDirectTermECP.code ||
      productCode === Product.products.MMTRADTermECP.code
    );
  }

  static isWopEligible(policy: Policy): boolean {
    // If dob is not available, return false.
    if (!policy.insured.person.dob) {
      return false;
    }
    const insuredDob: Date = new Date(policy.insured.person.dob);
    const isSmoker: boolean = policy.application.quote ? this.isSmokerHealthClassFromPolicy(policy) : false;

    if (policy.isTermLifePolicy()) {
      if (policy.isEcpProduct()) {
        if (isSmoker && policy.product === Product.products.CASTermECP.code && policy.term === 30) {
          return (
            CommonUtils.isOlderThanOrEqualToAge(insuredDob, Product.productSpecs.CASTermECP.minAge) &&
            CommonUtils.isYoungerThanAge(
              insuredDob,
              Product.products.CASTermECP.productSpecs.termBreakdown[policy.term].tobaccoMaxAge
            )
          );
        }
        return (
          CommonUtils.isOlderThanOrEqualToAge(insuredDob, Product.productSpecs.CASTermECP.minAge) &&
          CommonUtils.isYoungerThanAge(
            insuredDob,
            Product.products.CASTermECP.productSpecs.termBreakdown[policy.term].maxAge
          )
        );
      } else {
        if (isSmoker) {
          let maxAge =
            Product.products.CASTerm.productSpecs.termBreakdown[policy.term].tobaccoMaxAge ||
            Product.products.CASTerm.productSpecs.termBreakdown[policy.term].maxAge;
          maxAge = Math.min(RiderConstants.wop.maxAge, maxAge);
          return (
            CommonUtils.isOlderThanOrEqualToAge(insuredDob, Product.productSpecs.CASTerm.minAge) &&
            CommonUtils.isYoungerThanAge(insuredDob, maxAge)
          );
        } else {
          const maxAge = Math.min(
            RiderConstants.wop.maxAge,
            Product.products.CASTerm.productSpecs.termBreakdown[policy.term].maxAge
          );
          return (
            CommonUtils.isOlderThanOrEqualToAge(insuredDob, RiderConstants.wop.minAge) &&
            CommonUtils.isYoungerThanAge(insuredDob, maxAge)
          );
        }
      }
    } else if (policy.isWholeLifePolicy()) {
      const maxAge = Math.min(
        RiderConstants.wop.maxAge,
        policy.faceAmount > Product.products[policy.product].productSpecs.maxFaceForHigherAge
          ? Product.products[policy.product].productSpecs.termBreakdown[policy.term][1].maxAge
          : Product.products[policy.product].productSpecs.termBreakdown[policy.term][0].maxAge
      );
      return (
        CommonUtils.isOlderThanOrEqualToAge(insuredDob, Product.productSpecs[policy.product].minAge) &&
        CommonUtils.isYoungerThanAge(insuredDob, maxAge)
      );
    }
    return false;
  }

  static isSmokerHealthClassFromPolicy(policy: Policy): boolean {
    return this.isSmokerHealthClass(policy.getQuoteHealthClass());
  }

  static isSmokerHealthClass(healthClass: string) {
    return (
      healthClass === RateClasses.SELECT_TOBACCO.stringCode || healthClass === RateClasses.STANDARD_TOBACCO.stringCode
    );
  }

  // For a whole life policy, if the face amount goes below 50k, the rate class can only be
  // standard or standard tobacco (depending on if the quote rate class was tobacco or non-tobacco)
  // unless the policy has a table rating or is not a product eligible for low face amounts
  static determineRatingClass(
    healthClass: string,
    faceAmount: number,
    product: string,
    productType: string
  ): RateClassObj {
    if (
      productType === ProductType.TERM ||
      faceAmount >= LowFaceWholeLife.maxFace ||
      !LowFaceWholeLife.availableProducts.includes(product) ||
      RateClasses.isTableRatedByStringCode(healthClass)
    ) {
      return RateClasses[healthClass];
    }

    return RateClasses[healthClass].smoker === true ? RateClasses.STANDARD_TOBACCO : RateClasses.STANDARD;
  }

  /**
   * Returns a string with paymentFrequency convention expected by a Coverpath quote object (e.g., Monthly, Annual)
   */
  static makePaymentFrequencySafeForCoverpathQuote(input: string): string {
    if (input === null || input === undefined || input === '') {
      return input;
    }

    if (this.annualRegex.test(input)) {
      return 'Annual';
    } else if (this.monthRegex.test(input)) {
      return 'Monthly';
    } else {
      throw Error(`Can't make ${input} safe for a paymentFrequency on a Coverpath quote or the Product Service`);
    }
  }

  static copySpouseInfo(policy: Policy) {
    /**
     * do nothing if 2020 CP/MMD filing
     *
     * changes were made to ask the bene reflexives question
     * even if the bene = "Spouse or Partner/Fiance"
     * AND the insured/owner is the "Spouse" or "Partner/Fiance"
     */
    if (policy.is2020CoverpathOrMMD()) {
      return;
    }

    // If beneficiary is spouse, fill bene with spouse info
    if (
      (policy.insuredType === 'spouse' || policy.insuredType === 'partnerfiance') &&
      policy.getBeneficiaryType() === Assumptions.beneficiaryTypes.Spouse &&
      (policy.ownerType === 'you' || policy.ownerType === 'spouse' || policy.ownerType === 'partnerfiance')
    ) {
      policy.copySpouseInfoToBene(policy.primaryBeneficiary[0]);
    } else if (
      (policy.insuredType === 'spouse' || policy.insuredType === 'partnerfiance') &&
      policy.getContingentBeneficiaryType() === Assumptions.beneficiaryTypes.Spouse &&
      (policy.ownerType === 'you' || policy.ownerType === 'spouse' || policy.ownerType === 'partnerfiance')
    ) {
      policy.copySpouseInfoToBene(policy.contingentBeneficiary[0]);
    }
  }

  // TODO: this is one example to change to use productType enum in HL-10650
  static getSupportedStateCodes(unsupportedStates: [string]): Array<string> {
    return Object.keys(Assumptions.stateValues).filter((stateCode) => {
      return unsupportedStates.indexOf(stateCode) === -1;
    });
  }

  static getSupportedStateValues(unsupportedStates: [string]): StateKeyValuePairs {
    const supportedStates = {};
    for (const stateCode in Assumptions.stateValues) {
      if (unsupportedStates.indexOf(stateCode) === -1) {
        supportedStates[stateCode] = Assumptions.stateValues[stateCode];
      }
    }
    return supportedStates;
  }

  /* This differs from policy.isCoverpathPolicy in that it does not rely on the class type.
   * This is useful when we are in a place where a generic type is used, such as workflow
   */
  static hasCoverpathProduct(policy: Policy): boolean {
    return policy.getChannelCode() === Product.channels.CAS.code;
  }

  static getApplicationFormName = function (policy: Policy, nodes?: Array<MetadataNode>): string {
    const stateConfigs = AvailableStateRegistry[policy.getChannelCode()][policy.getApplicationState()];

    let formNameMetadata;
    const metadataNode_formNumber = Assumptions.Metadata_Node_FormNumber;

    if (nodes instanceof Array) {
      nodes.some((node) => {
        if (node.children instanceof Array) {
          return node.children.some((child) => {
            if (child.id === metadataNode_formNumber) {
              formNameMetadata = child.qText;
              return true;
            }
          });
        }
      });
    }

    if (formNameMetadata) {
      return formNameMetadata;
    } else if (stateConfigs) {
      return `A2200HL-${policy.getApplicationState()}` + (stateConfigs.formId ? ` ${stateConfigs.formId}` : '');
    } else {
      UILoggerSingleton.getInstance().debug(
        {
          module: 'CommonUtils',
          function: 'getApplicationFormName',
          category: UICategory.request,
          data: {
            policyId: policy._id,
            policyNumber: policy.policyNumber,
          },
        },
        `Unable to find stateConfig from AvailableStateRegistry for policy with channel: ${policy.getChannelCode()} and state: ${policy.getApplicationState()}`
      );
    }

    return '';
  };

  static extractPiiConfigForApplication(config): Map<string, string> {
    const propertiesToDataElementMap = new Map<string, string>();

    for (const key in config) {
      const dataElementObj = config[key];
      for (const prop of dataElementObj.properties) {
        propertiesToDataElementMap.set(prop, key);
      }
    }
    return propertiesToDataElementMap;
  }

  /* Convert to generic property to handle properties based on an array.
   * generic prop will remain the same for non-array based properties, such as 'insured.person.SSN'
   * Example 1:
   * prop: 'trustOrOtherEntity.trustGrantorList[0].grantorPerson.SSN'
   * generic prop: 'trustOrOtherEntity.trustGrantorList[i].grantorPerson.SSN'
   * Example 2:
   * prop: 'insured.person.SSN'
   * generic prop: 'insured.person.SSN'
   */
  static convertToGenericProp(prop: string): string {
    if (!prop) {
      return prop;
    }

    let shortProp = prop.replace('customer.application.primaryPolicy.', '');
    shortProp = shortProp.replace('customer.application.secondaryPolicy.', '');

    if (!shortProp.includes('[')) {
      return shortProp;
    }

    return shortProp.replace(/\[\d+\]/g, '[i]');
  }

  public static parsePhoneNumber(value) {
    const parsedPhone = value.replace(/[^0-9]/g, '');
    return parsedPhone;
  }
}
